import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, Users, CalendarDays, BookOpen, ClipboardCheck,
  MessageSquare, LogOut, Plus, ChevronDown, ChevronUp, Star,
  Phone, Mail, Clock, Baby, School, CheckCircle2, XCircle, AlertCircle,
  Bell, Copy, Download, Key, Camera, FileText, Printer, Menu, X, Search,
  Trash2, Upload, Image, Video
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
});

const statusColors = {
  pending: 'bg-accent/15 text-accent-foreground border-accent/30',
  confirmed: 'bg-success/15 text-success-foreground border-success/30',
  active: 'bg-success/15 text-success-foreground border-success/30',
  completed: 'bg-secondary/15 text-secondary-foreground border-secondary/30',
  cancelled: 'bg-destructive/15 text-destructive border-destructive/30',
  inactive: 'bg-muted text-muted-foreground border-border',
  present: 'bg-success/15 text-success-foreground border-success/30',
  absent: 'bg-destructive/15 text-destructive border-destructive/30',
  late: 'bg-accent/15 text-accent-foreground border-accent/30',
};

const tabs = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'tours', label: 'Tours', icon: CalendarDays },
  { id: 'enrollments', label: 'Enrollments', icon: Users },
  { id: 'children', label: 'Children', icon: Baby },
  { id: 'progress', label: 'Progress', icon: BookOpen },
  { id: 'attendance', label: 'Attendance', icon: ClipboardCheck },
  { id: 'communications', label: 'Messages', icon: MessageSquare },
  { id: 'forms', label: 'Forms', icon: FileText },
  { id: 'gallery', label: 'Gallery', icon: Camera },
];

// ─── Rating Stars Component ─────────────────────────────
const RatingStars = ({ value, onChange, readonly }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        disabled={readonly}
        onClick={() => onChange?.(n)}
        className={`transition-all ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
      >
        <Star className={`w-5 h-5 ${n <= value ? 'text-accent fill-accent' : 'text-border'}`} />
      </button>
    ))}
  </div>
);

// ─── Stat Card ──────────────────────────────────────────
const statStyles = {
  red: { bg: '#FEE2E2', border: '#FECACA', iconBg: '#FCA5A5', iconColor: '#DC2626', accent: '#EF4444' },
  blue: { bg: '#DBEAFE', border: '#BFDBFE', iconBg: '#93C5FD', iconColor: '#2563EB', accent: '#3B82F6' },
  green: { bg: '#DCFCE7', border: '#BBF7D0', iconBg: '#86EFAC', iconColor: '#16A34A', accent: '#22C55E' },
  orange: { bg: '#FFF7ED', border: '#FED7AA', iconBg: '#FDBA74', iconColor: '#EA580C', accent: '#F97316' },
  purple: { bg: '#F3E8FF', border: '#E9D5FF', iconBg: '#C4B5FD', iconColor: '#7C3AED', accent: '#8B5CF6' },
  pink: { bg: '#FCE7F3', border: '#FBCFE8', iconBg: '#F9A8D4', iconColor: '#DB2777', accent: '#EC4899' },
  teal: { bg: '#CCFBF1', border: '#99F6E4', iconBg: '#5EEAD4', iconColor: '#0D9488', accent: '#14B8A6' },
};

const StatCard = ({ label, value, icon: Icon, colorKey, onClick }) => {
  const s = statStyles[colorKey] || statStyles.blue;
  return (
    <div
      className={`rounded-2xl p-5 space-y-2 ${onClick ? 'cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all active:scale-[0.98]' : ''}`}
      style={{ background: s.bg, border: `2px solid ${s.border}` }}
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
      onClick={onClick}
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: s.iconBg }}>
        <Icon className="w-5 h-5" style={{ color: s.iconColor }} />
      </div>
      <div className="font-fredoka font-bold text-2xl text-foreground">{value}</div>
      <div className="text-sm font-medium" style={{ color: s.accent }}>{label}</div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [tours, setTours] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [children, setChildren] = useState([]);
  const [progress, setProgress] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [notifications, setNotifications] = useState({ new_tours: 0, new_enrollments: 0 });
  const [menuOpen, setMenuOpen] = useState(false);

  const authCheck = useCallback(async () => {
    try {
      await axios.get(`${API}/admin/verify`, getAuthHeader());
    } catch {
      localStorage.removeItem('admin_token');
      navigate('/admin');
    }
  }, [navigate]);

  const fetchData = useCallback(async () => {
    try {
      const [s, t, e, ch, p, a, co, notif] = await Promise.all([
        axios.get(`${API}/admin/stats`, getAuthHeader()),
        axios.get(`${API}/admin/tour-requests`, getAuthHeader()),
        axios.get(`${API}/admin/enrollments`, getAuthHeader()),
        axios.get(`${API}/admin/children`, getAuthHeader()),
        axios.get(`${API}/admin/progress`, getAuthHeader()),
        axios.get(`${API}/admin/attendance${attendanceDate ? `?date=${attendanceDate}` : ''}`, getAuthHeader()),
        axios.get(`${API}/admin/communications`, getAuthHeader()),
        axios.get(`${API}/admin/notifications`, getAuthHeader()),
      ]);
      setStats(s.data);
      setTours(t.data);
      setEnrollments(e.data);
      setChildren(ch.data);
      setProgress(p.data);
      setAttendance(a.data);
      setCommunications(co.data);
      setNotifications(notif.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    }
  }, [navigate, attendanceDate]);

  useEffect(() => { authCheck(); }, [authCheck]);
  useEffect(() => { fetchData(); }, [fetchData, activeTab]);

  const logout = () => {
    localStorage.removeItem('admin_token');
    navigate('/');
    toast.success('Logged out');
  };

  const updateStatus = async (collection, id, status) => {
    try {
      await axios.put(`${API}/admin/${collection}/${id}?status=${status}`, {}, getAuthHeader());
      toast.success('Status updated');
      fetchData();
    } catch {
      toast.error('Failed to update');
    }
  };

  const markNotificationsRead = async () => {
    await axios.post(`${API}/admin/notifications/mark-read`, {}, getAuthHeader());
    setNotifications({ new_tours: 0, new_enrollments: 0 });
  };

  const totalNotifications = notifications.new_tours + notifications.new_enrollments;

  const copyAccessCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Access code copied!');
  };

  const exportChildProgressPDF = (child) => {
    const childProgress = progress.filter((p) => p.child_id === child.id);
    if (childProgress.length === 0) { toast.error('No progress reports for this child'); return; }
    const doc = new jsPDF();
    const cats = [
      { key: 'social_skills', label: 'Social Skills' },
      { key: 'academic', label: 'Academic' },
      { key: 'behavior', label: 'Behavior' },
      { key: 'physical_development', label: 'Physical Development' },
    ];
    doc.setFontSize(20); doc.setTextColor(220, 100, 100); doc.text("Hooyo's Place", 14, 20);
    doc.setFontSize(10); doc.setTextColor(100); doc.text("Early Education & Childcare", 14, 27);
    doc.setFontSize(16); doc.setTextColor(40); doc.text(`Progress Report - ${child.name}`, 14, 42);
    doc.setFontSize(10); doc.text(`Age: ${child.age} | Program: ${child.program} | Generated: ${new Date().toLocaleDateString()}`, 14, 50);
    let y = 60;
    childProgress.forEach((report) => {
      if (y > 250) { doc.addPage(); y = 20; }
      doc.setFontSize(12); doc.setTextColor(40); doc.text(`Report - ${report.date}`, 14, y); y += 8;
      const rows = cats.map((c) => {
        const stars = '\u2605'.repeat(report[`${c.key}_rating`]) + '\u2606'.repeat(5 - report[`${c.key}_rating`]);
        return [c.label, stars, report[`${c.key}_notes`] || '-'];
      });
      autoTable(doc, { startY: y, head: [['Category', 'Rating', 'Notes']], body: rows, styles: { fontSize: 9 }, headStyles: { fillColor: [220, 100, 100] }, theme: 'grid', margin: { left: 14 } });
      y = doc.lastAutoTable.finalY + 5;
      if (report.general_notes) { doc.setFontSize(9); doc.setTextColor(80); doc.text(`Notes: ${report.general_notes}`, 14, y, { maxWidth: 180 }); y += 12; }
      y += 5;
    });
    doc.save(`${child.name}_Progress.pdf`);
    toast.success('PDF downloaded!');
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-border/50 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="font-fredoka font-bold text-xl">
            <span className="text-primary">Hoo</span>
            <span className="text-secondary">yo's</span>
            <span className="text-accent"> Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { markNotificationsRead(); setActiveTab('tours'); }}
              className="relative p-2 rounded-full hover:bg-muted transition-all"
              data-testid="admin-notifications-btn"
            >
              <Bell className="w-5 h-5 text-muted-foreground" />
              {totalNotifications > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-destructive text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {totalNotifications}
                </span>
              )}
            </button>
            <Button variant="ghost" onClick={logout} className="text-muted-foreground hover:text-destructive rounded-full" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Hamburger Nav */}
        <div className="relative mb-6">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex items-center gap-3 px-4 py-3 bg-white rounded-2xl border border-border/50 shadow-soft w-full md:w-auto transition-all hover:shadow-md"
            data-testid="admin-menu-toggle"
          >
            {menuOpen ? <X className="w-5 h-5 text-primary" /> : <Menu className="w-5 h-5 text-primary" />}
            <span className="font-bold text-foreground">
              {tabs.find(t => t.id === activeTab)?.label || 'Menu'}
            </span>
            <ChevronDown className={`w-4 h-4 text-muted-foreground ml-auto transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
          </button>

          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.15 }}
              className="absolute top-full left-0 right-0 md:right-auto md:min-w-[260px] mt-2 bg-white rounded-2xl border border-border/40 shadow-elevated z-50 overflow-hidden"
              data-testid="admin-menu-dropdown"
            >
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setShowForm(false); setMenuOpen(false); }}
                    className={`flex items-center gap-3 w-full px-5 py-3 text-sm font-semibold transition-all ${
                      activeTab === tab.id
                        ? 'bg-primary/10 text-primary'
                        : 'text-foreground/70 hover:bg-muted/60'
                    }`}
                    data-testid={`admin-tab-${tab.id}`}
                  >
                    <Icon className={`w-4 h-4 ${activeTab === tab.id ? 'text-primary' : 'text-muted-foreground'}`} />
                    {tab.label}
                  </button>
                );
              })}
            </motion.div>
          )}
        </div>

        {/* ─── OVERVIEW ──────────────────────────────── */}
        {activeTab === 'overview' && stats && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <h2 className="font-fredoka font-bold text-2xl text-foreground" data-testid="admin-overview-heading">Dashboard Overview</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Active Children" value={stats.active_children} icon={Baby} colorKey="blue" onClick={() => setActiveTab('children')} />
              <StatCard label="Enrollments" value={stats.total_enrollments} icon={Users} colorKey="green" onClick={() => setActiveTab('enrollments')} />
              <StatCard label="Pending Tours" value={stats.pending_tours} icon={CalendarDays} colorKey="orange" onClick={() => setActiveTab('tours')} />
              <StatCard label="Present Today" value={stats.present_today} icon={ClipboardCheck} colorKey="teal" onClick={() => setActiveTab('attendance')} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard label="Total Tours" value={stats.total_tours} icon={CalendarDays} colorKey="purple" onClick={() => setActiveTab('tours')} />
              <StatCard label="Progress Reports" value={stats.total_progress_reports} icon={BookOpen} colorKey="pink" onClick={() => setActiveTab('progress')} />
              <StatCard label="Communications" value={stats.total_communications} icon={MessageSquare} colorKey="red" onClick={() => setActiveTab('communications')} />
            </div>
          </motion.div>
        )}

        {/* ─── TOURS ─────────────────────────────────── */}
        {activeTab === 'tours' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <h2 className="font-fredoka font-bold text-2xl text-foreground">Tour Requests</h2>
            {tours.length === 0 ? <EmptyState message="No tour requests yet" /> : (
              <div className="space-y-3">
                {tours.map((t) => (
                  <div key={t.id} className="bg-white rounded-2xl p-5 border border-border/40 shadow-soft space-y-3" data-testid={`tour-item-${t.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-bold text-foreground">{t.parent_name}</div>
                        <div className="flex gap-4 text-sm text-muted-foreground mt-1">
                          <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{t.phone}</span>
                          <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{t.email}</span>
                        </div>
                      </div>
                      <Badge className={`${statusColors[t.status]} rounded-full px-3`}>{t.status}</Badge>
                    </div>
                    <div className="flex gap-4 text-sm">
                      <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3 text-secondary" />{t.preferred_date}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-secondary" />{t.preferred_time}</span>
                      <span>{t.num_children} child(ren)</span>
                    </div>
                    {t.message && <p className="text-sm text-muted-foreground bg-muted/50 rounded-xl p-3">{t.message}</p>}
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => updateStatus('tour-requests', t.id, 'confirmed')} className="rounded-full bg-success/15 text-success hover:bg-success/25 border-0 text-xs h-8">Confirm</Button>
                      <Button size="sm" onClick={() => updateStatus('tour-requests', t.id, 'completed')} className="rounded-full bg-secondary/15 text-secondary hover:bg-secondary/25 border-0 text-xs h-8">Complete</Button>
                      <Button size="sm" onClick={() => updateStatus('tour-requests', t.id, 'cancelled')} className="rounded-full bg-destructive/15 text-destructive hover:bg-destructive/25 border-0 text-xs h-8">Cancel</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* ─── ENROLLMENTS ───────────────────────────── */}
        {activeTab === 'enrollments' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <h2 className="font-fredoka font-bold text-2xl text-foreground">Enrollments</h2>
            {enrollments.length === 0 ? <EmptyState message="No enrollments yet" /> : (
              <div className="space-y-3">
                {enrollments.map((e) => (
                  <div key={e.id} className="bg-white rounded-2xl p-5 border border-border/40 shadow-soft space-y-3" data-testid={`enrollment-item-${e.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-bold text-foreground">{e.child_name} <span className="text-muted-foreground font-normal text-sm">(Age {e.child_age})</span></div>
                        <div className="text-sm text-muted-foreground">Parent: {e.parent_name} &middot; {e.phone} &middot; {e.email}</div>
                      </div>
                      <Badge className={`${statusColors[e.status]} rounded-full px-3`}>{e.status}</Badge>
                    </div>
                    <div className="flex gap-4 text-sm">
                      <span>Program: {e.age_group}</span>
                      <span>Start: {e.start_date}</span>
                    </div>
                    {e.message && <p className="text-sm text-muted-foreground bg-muted/50 rounded-xl p-3">{e.message}</p>}
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => updateStatus('enrollments', e.id, 'confirmed')} className="rounded-full bg-success/15 text-success hover:bg-success/25 border-0 text-xs h-8">Confirm</Button>
                      <Button size="sm" onClick={() => updateStatus('enrollments', e.id, 'active')} className="rounded-full bg-secondary/15 text-secondary hover:bg-secondary/25 border-0 text-xs h-8">Activate</Button>
                      <Button size="sm" onClick={() => updateStatus('enrollments', e.id, 'cancelled')} className="rounded-full bg-destructive/15 text-destructive hover:bg-destructive/25 border-0 text-xs h-8">Cancel</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* ─── CHILDREN ──────────────────────────────── */}
        {activeTab === 'children' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-fredoka font-bold text-2xl text-foreground">Children</h2>
              <Button onClick={() => setShowForm(!showForm)} className="rounded-full bg-primary hover:bg-primary/90 text-white" data-testid="add-child-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Child
              </Button>
            </div>
            {showForm && <ChildForm onSave={() => { setShowForm(false); fetchData(); }} />}
            {children.length === 0 && !showForm ? <EmptyState message="No children added yet. Add your first child above!" /> : (
              <div className="grid md:grid-cols-2 gap-4">
                {children.map((c) => (
                  <div key={c.id} className="bg-white rounded-2xl p-5 border border-border/40 shadow-soft space-y-3" data-testid={`child-card-${c.id}`}>
                    <div className="flex justify-between items-start">
                      <div className="flex gap-3 items-center">
                        {/* Child Photo */}
                        <div className="relative group">
                          {c.photo_path ? (
                            <img src={`${API}/files/${c.photo_path}`} alt={c.name} className="w-14 h-14 rounded-full object-cover border-2 border-primary/20" />
                          ) : (
                            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center border-2 border-primary/20">
                              <span className="text-lg font-bold text-primary">{c.name?.charAt(0)}</span>
                            </div>
                          )}
                          <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                            <Camera className="w-5 h-5 text-white" />
                            <input type="file" accept="image/*" className="hidden" data-testid={`upload-photo-${c.id}`}
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const formData = new FormData();
                                formData.append('file', file);
                                try {
                                  await axios.post(`${API}/admin/children/${c.id}/photo`, formData, {
                                    ...getAuthHeader(),
                                    headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' },
                                  });
                                  toast.success('Photo uploaded!');
                                  fetchData();
                                } catch { toast.error('Failed to upload photo'); }
                              }}
                            />
                          </label>
                        </div>
                        <div>
                          <div className="font-bold text-lg text-foreground flex items-center gap-2">
                            {c.program === 'early-learners' ? <Baby className="w-4 h-4 text-primary" /> : <School className="w-4 h-4 text-secondary" />}
                            {c.name}
                          </div>
                          <div className="text-sm text-muted-foreground">Age {c.age} &middot; {c.program}</div>
                        </div>
                      </div>
                      <Badge className={`${statusColors[c.status]} rounded-full px-3`}>{c.status}</Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <div>Parent: {c.parent_name}</div>
                      {c.parent_phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.parent_phone}</div>}
                    </div>
                    {c.notes && <p className="text-xs text-muted-foreground bg-muted/50 rounded-xl p-2">{c.notes}</p>}
                    {/* Access Code + Actions */}
                    <div className="flex items-center justify-between pt-1 border-t border-border/30">
                      <div className="flex items-center gap-2">
                        <Key className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Parent Code:</span>
                        <code className="bg-muted/50 px-2 py-0.5 rounded text-xs font-mono font-bold" data-testid={`child-access-code-${c.id}`}>{c.access_code}</code>
                        <button onClick={() => copyAccessCode(c.access_code)} className="p-1 rounded hover:bg-muted transition-all" data-testid={`copy-code-${c.id}`}>
                          <Copy className="w-3 h-3 text-muted-foreground" />
                        </button>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => exportChildProgressPDF(c)} className="rounded-full text-xs h-7 gap-1" data-testid={`export-child-pdf-${c.id}`}>
                        <Download className="w-3 h-3" /> PDF
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* ─── PROGRESS ──────────────────────────────── */}
        {activeTab === 'progress' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-fredoka font-bold text-2xl text-foreground">Progress Reports</h2>
              <Button onClick={() => setShowForm(!showForm)} className="rounded-full bg-primary hover:bg-primary/90 text-white" data-testid="add-progress-btn">
                <Plus className="w-4 h-4 mr-1" /> New Report
              </Button>
            </div>
            {showForm && <ProgressForm children={children} onSave={() => { setShowForm(false); fetchData(); }} />}
            {progress.length === 0 && !showForm ? <EmptyState message="No progress reports yet" /> : (
              <div className="space-y-3">
                {progress.map((p) => (
                  <ProgressCard key={p.id} report={p} />
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* ─── ATTENDANCE ────────────────────────────── */}
        {activeTab === 'attendance' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="font-fredoka font-bold text-2xl text-foreground">Attendance</h2>
              <Input type="date" value={attendanceDate} onChange={(e) => setAttendanceDate(e.target.value)} className="w-44 rounded-full border-2 h-10" data-testid="attendance-date-picker" />
            </div>
            {children.length === 0 ? <EmptyState message="Add children first to track attendance" /> : (
              <div className="space-y-3">
                {children.filter(c => c.status === 'active').map((child) => {
                  const record = attendance.find((a) => a.child_id === child.id);
                  return (
                    <AttendanceRow key={child.id} child={child} record={record} date={attendanceDate} onUpdate={fetchData} />
                  );
                })}
              </div>
            )}
          </motion.div>
        )}

        {/* ─── COMMUNICATIONS ────────────────────────── */}
        {activeTab === 'communications' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-fredoka font-bold text-2xl text-foreground">Communication Log</h2>
              <Button onClick={() => setShowForm(!showForm)} className="rounded-full bg-primary hover:bg-primary/90 text-white" data-testid="add-comm-btn">
                <Plus className="w-4 h-4 mr-1" /> New Entry
              </Button>
            </div>
            {showForm && <CommunicationForm children={children} onSave={() => { setShowForm(false); fetchData(); }} />}
            {communications.length === 0 && !showForm ? <EmptyState message="No communication logs yet" /> : (
              <div className="space-y-3">
                {communications.map((c) => (
                  <div key={c.id} className="bg-white rounded-2xl p-5 border border-border/40 shadow-soft" data-testid={`comm-item-${c.id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-bold text-foreground">{c.subject}</div>
                        <div className="text-sm text-muted-foreground">
                          {c.parent_name} {c.child_name ? `(${c.child_name})` : ''} &middot; {c.date}
                        </div>
                      </div>
                      <Badge className="bg-secondary/15 text-secondary rounded-full px-3">{c.comm_type}</Badge>
                    </div>
                    {c.notes && <p className="text-sm text-muted-foreground bg-muted/50 rounded-xl p-3">{c.notes}</p>}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* ─── FORMS ──────────────────────────────────── */}
        {activeTab === 'forms' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <h2 className="font-fredoka font-bold text-2xl text-foreground" data-testid="admin-forms-heading">Policy Forms & Documents</h2>
            <FormsSection />
          </motion.div>
        )}

        {activeTab === 'gallery' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <h2 className="font-fredoka font-bold text-2xl text-foreground" data-testid="admin-gallery-heading">Tour Gallery</h2>
            <p className="text-muted-foreground text-sm">Upload photos and videos of your space. These will appear on the Schedule a Tour page for parents to see.</p>
            <GallerySection />
          </motion.div>
        )}
      </div>
    </div>
  );
};

// ─── Empty State ────────────────────────────────────────
const EmptyState = ({ message }) => (
  <div className="bg-white rounded-2xl p-10 border border-border/40 text-center" data-testid="empty-state">
    <AlertCircle className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
    <p className="text-muted-foreground font-medium">{message}</p>
  </div>
);

// ─── Child Form ─────────────────────────────────────────
const ChildForm = ({ onSave }) => {
  const [form, setForm] = useState({ name: '', age: '', program: '', parent_name: '', parent_email: '', parent_phone: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/children`, { ...form, age: parseInt(form.age) }, getAuthHeader());
      toast.success('Child added!');
      onSave();
    } catch { toast.error('Failed to add child'); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl p-6 border border-border/40 shadow-soft space-y-4" data-testid="child-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1"><Label>Child Name *</Label><Input name="name" required value={form.name} onChange={handle} className="rounded-xl h-10 border-2" data-testid="child-form-name" /></div>
        <div className="space-y-1"><Label>Age *</Label><Input name="age" type="number" min="1" max="10" required value={form.age} onChange={handle} className="rounded-xl h-10 border-2" data-testid="child-form-age" /></div>
        <div className="space-y-1">
          <Label>Program *</Label>
          <Select value={form.program} onValueChange={(v) => setForm({ ...form, program: v })}>
            <SelectTrigger className="rounded-xl h-10 border-2" data-testid="child-form-program"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="early-learners">Early Learners (1-5)</SelectItem>
              <SelectItem value="after-school">After School (6-10)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1"><Label>Parent Name *</Label><Input name="parent_name" required value={form.parent_name} onChange={handle} className="rounded-xl h-10 border-2" data-testid="child-form-parent" /></div>
        <div className="space-y-1"><Label>Parent Email</Label><Input name="parent_email" type="email" value={form.parent_email} onChange={handle} className="rounded-xl h-10 border-2" /></div>
        <div className="space-y-1"><Label>Parent Phone</Label><Input name="parent_phone" value={form.parent_phone} onChange={handle} className="rounded-xl h-10 border-2" /></div>
      </div>
      <div className="space-y-1"><Label>Notes</Label><Textarea name="notes" rows={2} value={form.notes} onChange={handle} className="rounded-xl border-2 resize-none" /></div>
      <Button type="submit" disabled={saving} className="rounded-full bg-primary hover:bg-primary/90 text-white px-8" data-testid="child-form-submit">{saving ? 'Saving...' : 'Add Child'}</Button>
    </form>
  );
};

// ─── Progress Form ──────────────────────────────────────
const ProgressForm = ({ children, onSave }) => {
  const [form, setForm] = useState({
    child_id: '', child_name: '', date: new Date().toISOString().split('T')[0],
    social_skills_rating: 3, social_skills_notes: '',
    academic_rating: 3, academic_notes: '',
    behavior_rating: 3, behavior_notes: '',
    physical_development_rating: 3, physical_development_notes: '',
    general_notes: '',
  });
  const [saving, setSaving] = useState(false);

  const selectChild = (id) => {
    const child = children.find((c) => c.id === id);
    setForm({ ...form, child_id: id, child_name: child?.name || '' });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/progress`, form, getAuthHeader());
      toast.success('Progress report saved!');
      onSave();
    } catch { toast.error('Failed to save report'); }
    finally { setSaving(false); }
  };

  const categories = [
    { key: 'social_skills', label: 'Social Skills' },
    { key: 'academic', label: 'Academic' },
    { key: 'behavior', label: 'Behavior' },
    { key: 'physical_development', label: 'Physical Development' },
  ];

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl p-6 border border-border/40 shadow-soft space-y-5" data-testid="progress-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Child *</Label>
          <Select value={form.child_id} onValueChange={selectChild}>
            <SelectTrigger className="rounded-xl h-10 border-2" data-testid="progress-form-child"><SelectValue placeholder="Select child" /></SelectTrigger>
            <SelectContent>{children.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1"><Label>Date *</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl h-10 border-2" data-testid="progress-form-date" /></div>
      </div>
      {categories.map(({ key, label }) => (
        <div key={key} className="bg-muted/30 rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between">
            <Label className="font-bold">{label}</Label>
            <RatingStars value={form[`${key}_rating`]} onChange={(v) => setForm({ ...form, [`${key}_rating`]: v })} />
          </div>
          <Input placeholder={`Notes on ${label.toLowerCase()}...`} value={form[`${key}_notes`]} onChange={(e) => setForm({ ...form, [`${key}_notes`]: e.target.value })} className="rounded-xl border-2 h-9 text-sm" />
        </div>
      ))}
      <div className="space-y-1"><Label className="font-bold">General Notes</Label><Textarea value={form.general_notes} onChange={(e) => setForm({ ...form, general_notes: e.target.value })} rows={3} className="rounded-xl border-2 resize-none" data-testid="progress-form-notes" /></div>
      <Button type="submit" disabled={saving} className="rounded-full bg-primary hover:bg-primary/90 text-white px-8" data-testid="progress-form-submit">{saving ? 'Saving...' : 'Save Report'}</Button>
    </form>
  );
};

// ─── Progress Card ──────────────────────────────────────
const ProgressCard = ({ report }) => {
  const [expanded, setExpanded] = useState(false);
  const categories = [
    { key: 'social_skills', label: 'Social Skills' },
    { key: 'academic', label: 'Academic' },
    { key: 'behavior', label: 'Behavior' },
    { key: 'physical_development', label: 'Physical Development' },
  ];
  return (
    <div className="bg-white rounded-2xl p-5 border border-border/40 shadow-soft" data-testid={`progress-item-${report.id}`}>
      <div className="flex justify-between items-start cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div>
          <div className="font-bold text-foreground">{report.child_name}</div>
          <div className="text-sm text-muted-foreground">{report.date}</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            {categories.map(({ key }) => (
              <div key={key} className={`w-2 h-2 rounded-full ${report[`${key}_rating`] >= 4 ? 'bg-success' : report[`${key}_rating`] >= 3 ? 'bg-accent' : 'bg-destructive'}`} />
            ))}
          </div>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </div>
      {expanded && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-4 space-y-3">
          {categories.map(({ key, label }) => (
            <div key={key} className="flex items-center justify-between bg-muted/30 rounded-xl p-3">
              <div>
                <div className="text-sm font-semibold">{label}</div>
                {report[`${key}_notes`] && <div className="text-xs text-muted-foreground mt-1">{report[`${key}_notes`]}</div>}
              </div>
              <RatingStars value={report[`${key}_rating`]} readonly />
            </div>
          ))}
          {report.general_notes && <div className="bg-muted/30 rounded-xl p-3"><div className="text-sm font-semibold mb-1">General Notes</div><div className="text-sm text-muted-foreground">{report.general_notes}</div></div>}
        </motion.div>
      )}
    </div>
  );
};

// ─── Attendance Row ─────────────────────────────────────
const AttendanceRow = ({ child, record, date, onUpdate }) => {
  const markAttendance = async (status) => {
    try {
      await axios.post(`${API}/admin/attendance`, { child_id: child.id, child_name: child.name, date, status }, getAuthHeader());
      toast.success(`${child.name} marked ${status}`);
      onUpdate();
    } catch { toast.error('Failed to update attendance'); }
  };
  const current = record?.status;
  return (
    <div className="bg-white rounded-2xl p-4 border border-border/40 shadow-soft flex items-center justify-between" data-testid={`attendance-row-${child.id}`}>
      <div>
        <div className="font-bold text-foreground">{child.name}</div>
        <div className="text-sm text-muted-foreground">Age {child.age} &middot; {child.program}</div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={() => markAttendance('present')} className={`rounded-full text-xs h-8 ${current === 'present' ? 'bg-success text-white' : 'bg-success/15 text-success hover:bg-success/25'} border-0`} data-testid={`attendance-present-${child.id}`}>
          <CheckCircle2 className="w-3 h-3 mr-1" /> Present
        </Button>
        <Button size="sm" onClick={() => markAttendance('late')} className={`rounded-full text-xs h-8 ${current === 'late' ? 'bg-accent text-white' : 'bg-accent/15 text-accent-foreground hover:bg-accent/25'} border-0`}>
          <Clock className="w-3 h-3 mr-1" /> Late
        </Button>
        <Button size="sm" onClick={() => markAttendance('absent')} className={`rounded-full text-xs h-8 ${current === 'absent' ? 'bg-destructive text-white' : 'bg-destructive/15 text-destructive hover:bg-destructive/25'} border-0`} data-testid={`attendance-absent-${child.id}`}>
          <XCircle className="w-3 h-3 mr-1" /> Absent
        </Button>
      </div>
    </div>
  );
};

// ─── Communication Form ─────────────────────────────────
const CommunicationForm = ({ children, onSave }) => {
  const [form, setForm] = useState({ child_id: '', child_name: '', parent_name: '', date: new Date().toISOString().split('T')[0], comm_type: 'call', subject: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const selectChild = (id) => {
    const child = children.find((c) => c.id === id);
    setForm({ ...form, child_id: id, child_name: child?.name || '', parent_name: child?.parent_name || form.parent_name });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/communications`, form, getAuthHeader());
      toast.success('Communication logged!');
      onSave();
    } catch { toast.error('Failed to log communication'); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl p-6 border border-border/40 shadow-soft space-y-4" data-testid="comm-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1">
          <Label>Child (optional)</Label>
          <Select value={form.child_id} onValueChange={selectChild}>
            <SelectTrigger className="rounded-xl h-10 border-2"><SelectValue placeholder="Select child" /></SelectTrigger>
            <SelectContent>{children.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1"><Label>Parent Name *</Label><Input name="parent_name" required value={form.parent_name} onChange={handle} className="rounded-xl h-10 border-2" data-testid="comm-form-parent" /></div>
        <div className="space-y-1"><Label>Date *</Label><Input name="date" type="date" required value={form.date} onChange={handle} className="rounded-xl h-10 border-2" /></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Type *</Label>
          <Select value={form.comm_type} onValueChange={(v) => setForm({ ...form, comm_type: v })}>
            <SelectTrigger className="rounded-xl h-10 border-2" data-testid="comm-form-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="call">Phone Call</SelectItem>
              <SelectItem value="message">Text Message</SelectItem>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="in-person">In Person</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1"><Label>Subject *</Label><Input name="subject" required value={form.subject} onChange={handle} className="rounded-xl h-10 border-2" data-testid="comm-form-subject" /></div>
      </div>
      <div className="space-y-1"><Label>Notes</Label><Textarea name="notes" rows={3} value={form.notes} onChange={handle} className="rounded-xl border-2 resize-none" /></div>
      <Button type="submit" disabled={saving} className="rounded-full bg-primary hover:bg-primary/90 text-white px-8" data-testid="comm-form-submit">{saving ? 'Saving...' : 'Log Communication'}</Button>
    </form>
  );
};

// ─── Task Sheets Data ───────────────────────────────────
const taskSheets = [
  {
    id: 'all-about-me',
    title: 'All About Me',
    description: 'Worksheet: Name, age, family, where I\'m from, what I like to play, and goals',
    colorKey: 'pink',
    category: 'Writing',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/hix7w5t0_D0EF6EA2-6E38-4C9E-9D89-D39BDD18A522.png',
  },
  {
    id: 'all-about-me-2',
    title: 'All About Me - Fill In',
    description: 'Worksheet: My name, I am, I am a, I like to, my favorite color',
    colorKey: 'green',
    category: 'Writing',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/w71b7n4j_07EEEB35-14F0-4129-8116-74305BC9190B.png',
  },
  {
    id: 'color-correct-shapes',
    title: 'Color the Correct Shape',
    description: 'Shapes worksheet: Identify and color the correct triangle, heart, circle, square, and star',
    colorKey: 'orange',
    category: 'Shapes',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/gkjcudq0_F1AA658D-6ECA-48F5-919F-B3DA9212A8CF.png',
  },
  {
    id: 'compound-words',
    title: 'Compound Words',
    description: 'Phonics worksheet: Which two words make the compound word?',
    colorKey: 'blue',
    category: 'Phonics',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/kh1d8y13_97665A31-F444-4784-A25B-318DB41F9ECA.png',
  },
  {
    id: 'cvc-read-and-match',
    title: 'CVC Read and Match - Short A',
    description: 'Phonics worksheet: Cut out pictures and match to short a CVC words',
    colorKey: 'pink',
    category: 'Phonics',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/kmerexoy_09B41F3F-025E-4296-B5FD-EE8B96CE385D.png',
  },
  {
    id: 'cvc-word-fun',
    title: 'CVC Word Fun',
    description: 'Phonics worksheet: Look at pictures and write CVC words in boxes',
    colorKey: 'blue',
    category: 'Phonics',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/a8y93rez_517C172B-1C88-4F07-93EF-063866D1BC28.png',
  },
  {
    id: 'domino-addition',
    title: 'Domino Addition',
    description: 'Math worksheet: Domino dot counting and addition practice',
    colorKey: 'purple',
    category: 'Math',
    url: 'https://customer-assets.emergentagent.com/job_1f5fe23d-d8f2-4d46-9a46-cbe5186179e5/artifacts/28xcf6cf_54EBF704-7C69-4E79-B777-DA9190FD2C76.png',
  },
  {
    id: 'favorite-spring-break',
    title: 'Favorite Part of Spring Break',
    description: 'Creative writing prompt: Write about your favorite part of spring break',
    colorKey: 'green',
    category: 'Writing',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/re9ci30i_A90DE25A-D3BC-4B4B-90A6-F174F75FA70A.png',
  },
  {
    id: 'five-senses',
    title: 'Five Senses Worksheet',
    description: 'Science worksheet: Cut and paste the five senses in the correct boxes',
    colorKey: 'teal',
    category: 'Science',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/7ebq42ig_17B647C4-AABA-4CAF-AC62-FF7393DD8A1C.png',
  },
  {
    id: 'friends-reading',
    title: 'Friends - Reading Comprehension',
    description: 'Reading worksheet: Story about Tom and Sam with comprehension questions',
    colorKey: 'pink',
    category: 'Reading',
    url: 'https://customer-assets.emergentagent.com/job_1f5fe23d-d8f2-4d46-9a46-cbe5186179e5/artifacts/vgecp0ix_C286D8A1-9EC5-404D-AA52-5D345F35E0DC.png',
  },
  {
    id: 'healthy-vs-junk-food',
    title: 'Healthy Foods vs Junk Foods',
    description: 'Sorting activity: Cut and sort foods into healthy vs junk food categories',
    colorKey: 'orange',
    category: 'Science',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/qo8rc685_37836856-651F-4C58-A338-3ED6371D7E21.png',
  },
  {
    id: 'healthy-vs-junk-food-2',
    title: 'Healthy Foods vs Junk Foods - Sort & Label',
    description: 'Sorting activity: Draw or write foods in the correct healthy vs junk food box',
    colorKey: 'green',
    category: 'Science',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/d2emjha6_C4DD79C2-8F67-4838-9D11-22382F972DA7.png',
  },
  {
    id: 'how-many-fingers',
    title: 'How Many Fingers?',
    description: 'Counting worksheet: Circle the number that matches the fingers shown',
    colorKey: 'blue',
    category: 'Math',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/58i4v01r_44834485-363E-4F0A-A8EF-0973D2A28B6A.png',
  },
  {
    id: 'i-can-find-circles',
    title: 'I Can Find Circles',
    description: 'Shapes worksheet: Find and identify all the circle-shaped objects',
    colorKey: 'teal',
    category: 'Shapes',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/tsg6kmmt_47B1F696-8E82-4FDB-96BF-BC50796F4C81.png',
  },
  {
    id: 'i-can-find-squares',
    title: 'I Can Find Squares',
    description: 'Shapes worksheet: Find and identify all the square-shaped objects',
    colorKey: 'orange',
    category: 'Shapes',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/mfhrjuig_D8F0D1A5-3BEB-45E1-A5A4-6E32FD4B4C73.png',
  },
  {
    id: 'i-can-find-triangles',
    title: 'I Can Find Triangles',
    description: 'Shapes worksheet: Find and identify all the triangle-shaped objects',
    colorKey: 'blue',
    category: 'Shapes',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/cqtet5xl_9952CBCF-F026-402D-AB82-26843397B905.png',
  },
  {
    id: 'super-power',
    title: 'If I Had a Super Power',
    description: 'Creative writing prompt: What super power would you have?',
    colorKey: 'red',
    category: 'Writing',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/ythf1b1o_1278D737-AC18-48AB-94D5-9C518DC42874.png',
  },
  {
    id: 'matching-animals',
    title: 'Matching - Animals',
    description: 'Matching worksheet: Draw lines to match animal words to pictures',
    colorKey: 'teal',
    category: 'Reading',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/vjiygzzn_5742DF9F-77D8-4CDC-B987-EC2D732984D3.png',
  },
  {
    id: 'match-the-letters',
    title: 'Match the Letters',
    description: 'Alphabet worksheet: Draw lines to match capital letters to lowercase letters',
    colorKey: 'purple',
    category: 'Phonics',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/affedk4m_F378BAB0-A954-4BE3-8238-CC21C4C88996.png',
  },
  {
    id: 'monthly-activities',
    title: 'Monthly Activities',
    description: 'Week 1-4 curriculum: Colors & Shapes, Numbers & Counting, Letters & Reading, Kindness & Feelings',
    colorKey: 'teal',
    category: 'General',
    url: 'https://customer-assets.emergentagent.com/job_1f5fe23d-d8f2-4d46-9a46-cbe5186179e5/artifacts/z341tqb8_D75F861C-2C87-4024-8DF8-5329406082D8.png',
  },
  {
    id: 'now-enrolling',
    title: 'Now Enrolling Flyer',
    description: 'Enrollment info: Ages 1-5 (Morning 7AM-3PM), Ages 6-10 (Afternoon 3PM-8PM)',
    colorKey: 'purple',
    category: 'General',
    url: 'https://customer-assets.emergentagent.com/job_1f5fe23d-d8f2-4d46-9a46-cbe5186179e5/artifacts/1nz7er49_50F06EE9-CA23-4EDF-B9DC-0CC01F9C8A9E.png',
  },
  {
    id: 'rhyming-words',
    title: 'Rhyming Words',
    description: 'Phonics worksheet: Circle the word that rhymes with the word in the box',
    colorKey: 'pink',
    category: 'Phonics',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/1g3opwy0_F1D49E2F-8976-4240-B263-3921BD72CEF2.png',
  },
  {
    id: 'rhyming-words-write',
    title: 'Rhyming Words - Write a Match',
    description: 'Phonics worksheet: Write a word from the box that rhymes with the given word',
    colorKey: 'blue',
    category: 'Phonics',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/6x1dl1g4_2B942381-1C3F-4FB1-AACB-F17BE845D188.png',
  },
  {
    id: 'number-values',
    title: 'Number Values',
    description: 'Math worksheet: Color the number that has the bigger value',
    colorKey: 'orange',
    category: 'Math',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/a4wqm7x5_022E245F-1ABE-4DAD-8B39-523E5DF23F70.png',
  },
  {
    id: 'sentence-writing',
    title: 'Sentence Writing',
    description: 'Writing worksheet: Write each sentence correctly using capital letters, finger spaces, and punctuation',
    colorKey: 'green',
    category: 'Writing',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/v0a44my4_25DFFE65-8C3E-432E-818B-6D2AAB14656E.png',
  },
  {
    id: 'star-words',
    title: 'Star Words - CVC',
    description: 'Phonics worksheet: Color all the short "a" CVC words in the stars',
    colorKey: 'purple',
    category: 'Phonics',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/er6daupx_14C1E695-F787-408D-9C16-BFD1E84391AB.png',
  },
  {
    id: 'dinner-reading',
    title: 'Time For Dinner - Reading Comprehension',
    description: 'Reading worksheet: Dinner story with comprehension questions',
    colorKey: 'pink',
    category: 'Reading',
    url: 'https://customer-assets.emergentagent.com/job_1f5fe23d-d8f2-4d46-9a46-cbe5186179e5/artifacts/wb1jfxo5_EB119B59-5043-4CA4-B96A-A377F51FBFA7.png',
  },
  {
    id: 'what-should-i-write-about',
    title: 'What Should I Write About?',
    description: 'Creative writing prompt: Books, Places, People, Animals, or Things',
    colorKey: 'orange',
    category: 'Writing',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/c77nxmk9_9D7355DA-3D0E-4BDB-8996-539489640E76.png',
  },
  {
    id: 'when-i-grow-up',
    title: 'What Do I Want to Be When I Grow Up?',
    description: 'Creative writing prompt: Dream career writing activity',
    colorKey: 'orange',
    category: 'Writing',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/a942kn78_B811B187-9A3A-4EB7-B6EA-882804A01546.png',
  },
  {
    id: 'animal-identification',
    title: 'What Kind of Animal Is This?',
    description: 'Worksheet: Identify 9 animals (dog, elephant, monkey, cat, chick, panda, bunny, lion, tiger)',
    colorKey: 'teal',
    category: 'Science',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/3rfuqpka_87F45100-DD3B-4119-9258-2947E635B86D.png',
  },
  {
    id: 'whos-in-my-family',
    title: "Who's in My Family?",
    description: 'Creative writing prompt: Write about your family members',
    colorKey: 'blue',
    category: 'Writing',
    url: 'https://customer-assets.emergentagent.com/job_c1c56e56-332b-4a9d-83c4-7c61eedc8e17/artifacts/1axiw98d_AE5AC3A9-2AAE-4C94-BF9C-A0276378AD08.png',
  },
];

const sheetCategories = ['All', 'Writing', 'Phonics', 'Math', 'Shapes', 'Reading', 'Science', 'General'];
const categoryColors = {
  All: statStyles.blue,
  Writing: statStyles.pink,
  Phonics: statStyles.purple,
  Math: statStyles.orange,
  Shapes: statStyles.teal,
  Reading: statStyles.green,
  Science: statStyles.red,
  General: statStyles.blue,
};

// ─── Forms Section ──────────────────────────────────────
const formDocuments = [
  {
    id: 'business-information',
    title: "Hooyo's Place Business Information",
    colorKey: 'green',
    sections: [
      {
        heading: 'Setting Rules and Limits',
        items: [
          "At Hooyo's Place, we set and teach simple, consistent, clear, and positive rules and limits that children can easily understand by using kind words, gentle reminders, visual posters, daily routines, and age-appropriate examples. We model positive behavior such as sharing, listening, using safe hands, and showing respect for others. Rules are repeated consistently each day so children feel safe, know what to expect, and can learn healthy boundaries in a loving environment. Our goal is to guide children with patience and encouragement so they build confidence, responsibility, and positive social skills."
        ]
      },
      {
        heading: 'Environment for Success',
        items: [
          "At Hooyo's Place, we set up the environment for success by creating engaging, age-appropriate activities that encourage positive behavior and support self-regulation. Our learning spaces are organized, welcoming, and designed to help children feel safe, focused, and confident. Through hands-on activities such as sensory play, circle time, art, movement, reading, and guided group play, children are encouraged to practice patience, taking turns, problem-solving, and expressing their feelings in healthy ways. Predictable routines, calm spaces, and gentle guidance help children learn how to manage emotions, make positive choices, and build independence throughout the day."
        ]
      },
      {
        heading: 'Predictable Daily Routine',
        items: [
          "At Hooyo's Place, we follow a predictable daily routine and schedule with planned transitions to help children feel safe, secure, and prepared for each part of their day. Our daily schedule includes consistent times for arrival, meals, learning activities, outdoor play, rest, and pick-up. Transitions between activities are planned using verbal reminders, songs, visual cues, and countdowns so children know what to expect next. This consistent structure supports emotional security, reduces challenging behaviors, and helps children build independence, self-regulation, and confidence in their daily routines."
        ]
      },
      {
        heading: 'Positive Reinforcement',
        items: [
          "At Hooyo's Place, we reinforce positive behaviors with encouragement and descriptive praise by recognizing and acknowledging children's positive choices as they happen. Caregivers use kind, specific words such as \"Great job using your listening ears,\" \"Thank you for sharing with your friend,\" and \"I'm proud of how calmly you used your words.\" This descriptive praise helps children understand which behaviors are positive and encourages them to repeat those actions. Through consistent encouragement, children build confidence, self-esteem, and a strong sense of responsibility while learning respectful and caring behaviors."
        ]
      },
      {
        heading: 'Preventing Problems & Logical Consequences',
        items: [
          "At Hooyo's Place, we take proactive steps to prevent problems before they occur by maintaining clear expectations, consistent routines, close supervision, and engaging activities that support each child's needs and development. Caregivers gently guide children by reminding them of classroom rules, modeling appropriate behavior, and redirecting challenging behaviors before they escalate.",
          "When needed, we explain safe, natural, and logical consequences that are directly related to the child's behavior in a calm and age-appropriate way. For example, if toys are thrown, the toy may be put away for a short time and the child is guided to use it safely. This approach helps children understand the connection between their choices and outcomes while learning responsibility, self-control, and problem-solving skills."
        ]
      },
      {
        heading: 'Recognizing & Expressing Feelings',
        items: [
          "At Hooyo's Place, we help children recognize and appropriately express their feelings while also learning to understand the feelings of others by creating a safe, caring, and supportive environment. Caregivers use age-appropriate conversations, books, emotion charts, and daily interactions to help children identify feelings such as happy, sad, frustrated, excited, or upset. Children are encouraged to use words to express their emotions and needs in a respectful way. We also guide children in developing empathy by helping them notice how their actions may affect others and by encouraging kindness, sharing, and problem-solving with peers."
        ]
      },
      {
        heading: 'Teaching Social Skills',
        items: [
          "At Hooyo's Place, we model and teach social skills such as taking turns, cooperation, waiting patiently, treating others kindly, and problem-solving by using daily routines, guided play, and positive interactions. Caregivers demonstrate respectful communication, sharing, and teamwork through age-appropriate activities, group play, and classroom discussions. Children are gently guided to practice waiting their turn, using kind words, sharing materials, and working together with friends. When conflicts arise, caregivers support children in talking through the problem, expressing their feelings, and finding safe, respectful solutions."
        ]
      },
      {
        heading: 'Redirection & Guidance',
        items: [
          "At Hooyo's Place, we use redirection and gentle guidance to help a child change their focus when necessary by calmly guiding them toward a more appropriate activity, behavior, or environment. Caregivers observe children closely and step in early to redirect challenging behaviors before they escalate.",
          "Redirection is always done in a positive, respectful, and age-appropriate manner to help children make better choices while supporting emotional regulation, problem-solving, and positive behavior. We will ensure that only caregivers shall provide guidance to a child. Guidance will be provided that is fair, consistently applied, timely, and appropriate to the behavior, age, and development of the child."
        ]
      }
    ]
  },
  {
    id: 'business-information',
    title: "Hooyo's Place Business Information",
    colorKey: 'green',
    sections: [
      {
        heading: 'Operator',
        items: [
          "Name of Person with Immediate Responsibility for Daily Operation: Naimo Ali, Owner and Operator"
        ]
      },
      {
        heading: 'Business Details',
        items: [
          "Business Name: Hooyo's Place",
          "Business Type: Home-Based Early Learning and Child Care Program"
        ]
      },
      {
        heading: 'Contact Information',
        items: [
          "Business Address: 17960 SW Alvord Lane, Beaverton, Oregon 97006",
          "Business Telephone Number: 971-867-0536",
          "Email Address: Hooyosplace26@gmail.com",
          "Website: Hooyosplace.net",
          "Instagram: @Hooyosplace26"
        ]
      }
    ]
  },
  {
    id: 'parent-responsibilities',
    title: 'Parent Responsibilities and Required Information',
    colorKey: 'blue',
    sections: [
      {
        heading: 'Required Forms',
        items: [
          "Parents or guardians are responsible for providing all current and required information for their child before enrollment and throughout the child's attendance at Hooyo's Place.",
          "Required forms include, but are not limited to:",
          "Enrollment Form",
          "Infant and Toddler Additional Enrollment Information Form",
          "Medication Authorization Form",
          "Non-Prescription Medication Permission Form",
          "Allergy Care Plan",
          "Written Care Plan for any special medical, developmental, or dietary needs"
        ]
      },
      {
        heading: 'Keeping Information Current',
        items: [
          "Parents must ensure that all forms are completed accurately and updated whenever information changes, including emergency contacts, medical needs, allergies, and authorized pick-up persons."
        ]
      },
      {
        heading: 'Medication Policy',
        items: [
          "If a child requires prescribed medication, parents must provide the medication in its original, properly labeled container with clear instructions. Hooyo's Place cannot accept any medication that is not properly labeled."
        ]
      },
      {
        heading: 'Parent-Provided Supplies',
        items: [
          "For children in diapers, parents are responsible for providing an adequate supply of diapers and wipes.",
          "If a child requires specific foods, meals, snacks, formula, or breast milk, parents must provide those items daily as needed."
        ]
      },
      {
        heading: 'Communication & Collaboration',
        items: [
          "Hooyo's Place is committed to helping every child thrive. Parents are encouraged to share information about how their child learns best, what strategies have been helpful at home, and any important developmental or emotional needs.",
          "Parents or guardians are welcome to visit and spend time with their child during care hours without prior notice."
        ]
      },
      {
        heading: 'Pick-Up Authorization & Safety',
        items: [
          "For the safety of all children, only parents, guardians, or individuals authorized in writing may pick up a child. Any adult other than a parent or guardian must provide a valid state-issued photo identification card. Hooyo's Place will verify the person's identity by contacting the parent or guardian before releasing the child and may keep a copy or photo of the identification for records."
        ]
      }
    ]
  },
  {
    id: 'abuse-prevention-reporting',
    title: 'Prevention of and Duty to Report Suspected Child Abuse and Neglect',
    colorKey: 'red',
    sections: [
      {
        heading: 'Our Commitment',
        items: [
          "At Hooyo's Place, the safety, protection, and well-being of every child is our highest priority. Hooyo's Place is committed to preventing child abuse and neglect by maintaining a safe, nurturing, and closely supervised environment at all times. Children are cared for in a setting that promotes emotional security, physical safety, positive guidance, and respectful interactions."
        ]
      },
      {
        heading: 'Staff Responsibilities',
        items: [
          "All caregivers and staff members are expected to remain alert to any signs of suspected abuse or neglect, including physical injuries, unexplained marks, sudden changes in behavior, fearfulness, signs of inadequate care, or disclosures made by a child."
        ]
      },
      {
        heading: 'Mandatory Reporting',
        items: [
          "As a licensed child care provider in Oregon, Hooyo's Place is a mandatory reporter and is legally required to immediately report any reasonable suspicion of child abuse or neglect to the appropriate authorities. Reports must be made without delay to the Oregon Child Abuse Hotline or local law enforcement.",
          "Oregon Child Abuse Hotline: 1-855-503-SAFE (7233)",
          "Available 24 hours a day, 7 days a week"
        ]
      },
      {
        heading: 'When a Report Will Be Made',
        items: [
          "A report will be made whenever there is reasonable cause to believe that a child may be experiencing physical abuse, emotional abuse, sexual abuse, neglect, abandonment, or any other form of harm."
        ]
      },
      {
        heading: 'Investigation Policy',
        items: [
          "Hooyo's Place does not investigate suspected abuse. The responsibility of the provider is to report concerns immediately so that the proper authorities can assess the situation. All reports made in good faith are confidential and are made solely to protect the health and safety of the child.",
          "(Oregon)"
        ]
      }
    ]
  }
];

const FormsSection = () => {
  const [expanded, setExpanded] = useState({});
  const [viewSheet, setViewSheet] = useState(null);
  const [sheetCategory, setSheetCategory] = useState('All');
  const [sheetSearch, setSheetSearch] = useState('');

  const filteredSheets = taskSheets.filter((s) => {
    const matchCat = sheetCategory === 'All' || s.category === sheetCategory;
    const matchSearch = !sheetSearch || s.title.toLowerCase().includes(sheetSearch.toLowerCase());
    return matchCat && matchSearch;
  });

  const toggleDoc = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const printDoc = (doc) => {
    const printContent = doc.sections.map(s =>
      `<h3 style="margin:18px 0 8px;color:#333;font-size:16px;border-bottom:1px solid #ddd;padding-bottom:4px;">${s.heading}</h3>` +
      s.items.map(i => `<p style="margin:4px 0 4px 16px;font-size:13px;color:#444;line-height:1.6;">${i}</p>`).join('')
    ).join('');
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>${doc.title}</title></head><body style="max-width:700px;margin:40px auto;font-family:Georgia,serif;">
      <h1 style="text-align:center;color:#DC6464;font-size:22px;">Hooyo's Place</h1>
      <p style="text-align:center;color:#888;font-size:12px;">Early Education & Childcare</p>
      <h2 style="text-align:center;font-size:18px;margin:24px 0;color:#222;">${doc.title}</h2>
      ${printContent}
      <hr style="margin:30px 0;border:none;border-top:1px solid #ddd;">
      <p style="text-align:center;color:#999;font-size:11px;">Hooyo's Place Early Education & Childcare &mdash; ${new Date().toLocaleDateString()}</p>
    </body></html>`);
    win.document.close();
    win.print();
  };

  const printSheet = (sheet) => {
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>${sheet.title}</title><style>@media print{body{margin:0}img{width:100%;height:auto}}</style></head>
      <body style="margin:0;display:flex;justify-content:center;background:#fff;">
      <img src="${sheet.url}" style="max-width:100%;height:auto;" />
      </body></html>`);
    win.document.close();
    win.print();
  };

  return (
    <div className="space-y-6">
      {/* Policy Documents */}
      <div>
        <h3 className="font-fredoka font-bold text-lg text-foreground mb-3" data-testid="policy-docs-heading">Policy Documents</h3>
        <div className="space-y-3">
          {formDocuments.map((doc) => {
            const s = statStyles[doc.colorKey] || statStyles.blue;
            const isOpen = expanded[doc.id];
            return (
              <div key={doc.id} className="bg-white rounded-2xl border border-border/40 shadow-soft overflow-hidden" data-testid={`form-doc-${doc.id}`}>
                <div
                  className="flex items-center justify-between p-5 cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => toggleDoc(doc.id)}
                  data-testid={`form-toggle-${doc.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: s.iconBg }}>
                      <FileText className="w-5 h-5" style={{ color: s.iconColor }} />
                    </div>
                    <div>
                      <div className="font-bold text-foreground text-base">{doc.title}</div>
                      <div className="text-xs text-muted-foreground">{doc.sections.length} sections</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={(e) => { e.stopPropagation(); printDoc(doc); }} className="p-2 rounded-full hover:bg-muted transition-colors" title="Print" data-testid={`form-print-${doc.id}`}>
                      <Printer className="w-4 h-4 text-muted-foreground" />
                    </button>
                    {isOpen ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
                  </div>
                </div>
                {isOpen && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-5 pb-5 space-y-4 border-t border-border/30">
                    {doc.sections.map((section, idx) => (
                      <div key={idx} className="mt-4">
                        <h3 className="font-bold text-sm mb-2 flex items-center gap-2" style={{ color: s.accent }}>
                          <div className="w-1.5 h-1.5 rounded-full" style={{ background: s.accent }} />
                          {section.heading}
                        </h3>
                        <div className="space-y-1.5 pl-4">
                          {section.items.map((item, i) => (
                            <p key={i} className="text-sm text-muted-foreground leading-relaxed">{item}</p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Task Sheets */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-fredoka font-bold text-lg text-foreground" data-testid="task-sheets-heading">
            Task Sheets & Worksheets
            <span className="ml-2 text-sm font-normal text-muted-foreground">({filteredSheets.length})</span>
          </h3>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search worksheets..."
            value={sheetSearch}
            onChange={(e) => setSheetSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border-2 border-border/50 bg-white text-sm focus:outline-none focus:border-primary/40 transition-colors"
            data-testid="sheet-search-input"
          />
        </div>

        {/* Category pills */}
        <div className="flex gap-1.5 overflow-x-auto pb-3 mb-3 scrollbar-hide">
          {sheetCategories.map((cat) => {
            const count = cat === 'All' ? taskSheets.length : taskSheets.filter(s => s.category === cat).length;
            const cc = categoryColors[cat];
            return (
              <button
                key={cat}
                onClick={() => setSheetCategory(cat)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all ${
                  sheetCategory === cat ? 'text-white shadow-sm' : 'bg-white border border-border/50 text-foreground/70 hover:bg-muted/60'
                }`}
                style={sheetCategory === cat ? { background: cc.accent } : {}}
                data-testid={`sheet-cat-${cat.toLowerCase()}`}
              >
                {cat}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${sheetCategory === cat ? 'bg-white/25' : 'bg-muted'}`}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* Compact list */}
        {filteredSheets.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 border border-border/40 text-center">
            <p className="text-muted-foreground text-sm">No worksheets found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredSheets.map((sheet) => {
              const s = statStyles[sheet.colorKey] || statStyles.blue;
              return (
                <div
                  key={sheet.id}
                  className="bg-white rounded-xl border border-border/40 shadow-soft flex items-center gap-3 p-3 cursor-pointer hover:shadow-md hover:border-border/60 transition-all active:scale-[0.99]"
                  onClick={() => setViewSheet(sheet)}
                  data-testid={`task-sheet-${sheet.id}`}
                >
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-muted/20 flex-shrink-0">
                    <img src={sheet.url} alt={sheet.title} className="w-full h-full object-cover object-top" loading="lazy" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-foreground truncate">{sheet.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{sheet.description}</div>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-1 rounded-full flex-shrink-0" style={{ background: s.bg, color: s.accent, border: `1px solid ${s.border}` }}>{sheet.category}</span>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); setViewSheet(sheet); }}
                      className="p-1.5 rounded-lg hover:bg-muted/60 transition-colors"
                      title="View"
                      data-testid={`view-sheet-${sheet.id}`}
                    >
                      <BookOpen className="w-4 h-4 text-muted-foreground" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); printSheet(sheet); }}
                      className="p-1.5 rounded-lg hover:bg-muted/60 transition-colors"
                      title="Print"
                      data-testid={`print-sheet-${sheet.id}`}
                    >
                      <Printer className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Full-screen viewer modal */}
      {viewSheet && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setViewSheet(null)} data-testid="sheet-viewer-modal">
          <div className="relative max-w-3xl w-full max-h-[90vh] overflow-auto bg-white rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 flex items-center justify-between p-4 bg-white/95 backdrop-blur-sm border-b border-border/30 rounded-t-2xl">
              <h3 className="font-bold text-foreground text-sm">{viewSheet.title}</h3>
              <div className="flex gap-2">
                <button onClick={() => printSheet(viewSheet)} className="px-3 py-1.5 text-xs font-medium bg-primary text-white rounded-full hover:bg-primary/90 transition-colors flex items-center gap-1" data-testid="modal-print-btn">
                  <Printer className="w-3 h-3" /> Print
                </button>
                <button onClick={() => setViewSheet(null)} className="px-3 py-1.5 text-xs font-medium bg-muted text-foreground rounded-full hover:bg-muted/80 transition-colors" data-testid="modal-close-btn">
                  Close
                </button>
              </div>
            </div>
            <img src={viewSheet.url} alt={viewSheet.title} className="w-full h-auto" />
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Gallery Section ────────────────────────────────────────
const GallerySection = () => {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const fetchGallery = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/gallery`, authHeaders);
      setItems(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchGallery(); }, [fetchGallery]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    let successCount = 0;
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        await axios.post(`${API}/admin/gallery/upload`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}`, 'Content-Type': 'multipart/form-data' },
        });
        successCount++;
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (successCount > 0) toast.success(`${successCount} file${successCount > 1 ? 's' : ''} uploaded`);
    setUploading(false);
    fetchGallery();
    e.target.value = '';
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this media?')) return;
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, authHeaders);
      toast.success('Deleted');
      fetchGallery();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const photos = items.filter(i => i.media_type === 'photo');
  const videos = items.filter(i => i.media_type === 'video');

  return (
    <div className="space-y-6">
      {/* Upload */}
      <label className="flex flex-col items-center justify-center gap-3 p-8 bg-white rounded-2xl border-2 border-dashed border-border/60 hover:border-primary/40 cursor-pointer transition-colors" data-testid="gallery-upload-area">
        <input type="file" accept="image/*,video/*" multiple onChange={handleUpload} className="hidden" data-testid="gallery-file-input" />
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
          <Upload className="w-6 h-6 text-primary" />
        </div>
        <div className="text-center">
          <p className="font-bold text-foreground">{uploading ? 'Uploading...' : 'Click to upload photos or video'}</p>
          <p className="text-xs text-muted-foreground mt-1">Supports JPG, PNG, MP4, MOV</p>
        </div>
      </label>

      {/* Photos */}
      {photos.length > 0 && (
        <div>
          <h3 className="font-fredoka font-bold text-lg text-foreground mb-3 flex items-center gap-2">
            <Image className="w-5 h-5 text-secondary" /> Photos ({photos.length})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {photos.map((item) => (
              <div key={item.id} className="relative group rounded-xl overflow-hidden bg-muted/20 border border-border/40" data-testid={`gallery-photo-${item.id}`}>
                <img
                  src={`${API.replace('/api', '')}/api/files/${item.path}`}
                  alt={item.filename}
                  className="w-full aspect-square object-cover cursor-pointer"
                  onClick={() => setPreview(item)}
                  loading="lazy"
                />
                <button
                  onClick={() => handleDelete(item.id)}
                  className="absolute top-2 right-2 p-1.5 bg-red-500/90 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`gallery-delete-${item.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Videos */}
      {videos.length > 0 && (
        <div>
          <h3 className="font-fredoka font-bold text-lg text-foreground mb-3 flex items-center gap-2">
            <Video className="w-5 h-5 text-accent" /> Videos ({videos.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {videos.map((item) => (
              <div key={item.id} className="relative group rounded-xl overflow-hidden bg-black border border-border/40" data-testid={`gallery-video-${item.id}`}>
                <video
                  src={`${API.replace('/api', '')}/api/files/${item.path}`}
                  controls
                  className="w-full aspect-video"
                  preload="metadata"
                />
                <div className="flex items-center justify-between p-3 bg-white">
                  <span className="text-xs font-medium text-foreground truncate">{item.filename}</span>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    data-testid={`gallery-delete-${item.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {items.length === 0 && !uploading && (
        <div className="bg-white rounded-2xl p-10 border border-border/40 text-center">
          <Camera className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-muted-foreground font-medium">No media uploaded yet</p>
          <p className="text-xs text-muted-foreground mt-1">Upload photos and videos to showcase your space</p>
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="gallery-preview-modal">
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPreview(null)} className="absolute -top-10 right-0 text-white/80 hover:text-white text-sm font-medium" data-testid="gallery-preview-close">Close</button>
            <img src={`${API.replace('/api', '')}/api/files/${preview.path}`} alt={preview.filename} className="w-full h-auto rounded-xl" />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
